import { useMemo, useState, useEffect, Fragment } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import Layout from '../components/Layout';
import FormRow from '../components/FormRow';
import { useAuth } from '../context/AuthContext';
import {
  // projects
  listProjectsApi, createProjectApi, updateProjectApi, setProjectStatusApi,
  // members
  listProjectMembersApi, addProjectMemberApi, updateProjectMemberApi,
  // logs
  listProjectLogsApi,
  // clients & users
  listClientsApi, listUsersApi
} from '../api/http';
import { FaSyncAlt, FaBookOpen, FaUser, FaPen, FaUsers, FaPlus, FaRegClock, FaCalendarAlt, FaEye } from 'react-icons/fa';
import ModalShell from '../components/ModalShell';

// Canon de estados
const PROJECT_STATES = ['planned', 'active', 'on_hold', 'completed', 'archived'];
const STATUS_ALIASES = {
  'on-hold': 'on_hold',
  'on_hold': 'on_hold',
  'in-progress': 'active',
  'activo': 'active',
  'planificado': 'planned',
  'completado': 'completed',
  'archivado': 'archived'
};

// Normaliza
const normalizeStatus = (s) => {
  const key = String(s || '').toLowerCase().trim();
  return STATUS_ALIASES[key] || key;
};

// Etiquetas de estado
const STATUS_LABELS = {
  planned: 'Planificado',
  active: 'Activo',
  on_hold: 'En pausa',
  completed: 'Completado',
  archived: 'Archivado'
};

// Estilos
const btnIconRow = { display: 'inline-flex', alignItems: 'center', gap: '6px' };

const btnPrimary = {
  background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
  border: '1px solid #111827',
  color: '#ffffff',
  fontWeight: 600,
  transition: 'all 0.15s ease',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  padding: '8px 12px'
};

const btnSecondary = {
  background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
  border: '1px solid #d1d5db',
  color: '#374151',
  fontWeight: 500,
  transition: 'all 0.15s ease',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  borderRadius: '8px',
  padding: '8px 12px'
};

/* ====== UI Helpers ====== */
function StatusPill({ status }) {
  const canon = normalizeStatus(status);
  const label = STATUS_LABELS[canon] ?? status ?? '';
  const base = { display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 };

  const color =
    canon === 'active' ? { background: '#10b981', color: '#fff' } :
    canon === 'on_hold' ? { background: '#f59e0b', color: '#111' } :
    canon === 'completed' ? { background: '#3b82f6', color: '#fff' } :
    canon === 'archived' ? { background: '#6b7280', color: '#fff' } :
    { background: '#e5e7eb', color: '#111' };

  return <span style={{ ...base, ...color }}>{label}</span>;
}

function Banner({ kind = 'info', children, onClose }) {
  if (!children) return null;
  const s = kind === 'error' ? { bg: '#fee2e2', fg: '#991b1b', br: '#fecaca' } : { bg: '#dcfce7', fg: '#065f46', br: '#bbf7d0' };
  return (
    <div style={{ background: s.bg, color: s.fg, border: `1px solid ${s.br}`, padding: 10, borderRadius: 8, marginTop: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ overflowWrap: 'anywhere' }}>{children}</div>
      <button className="btn" onClick={onClose}>OK</button>
    </div>
  );
}

const fmtDate = (d) => {
  if (!d) return '';
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return s.slice(0, 10); // YYYY-MM-DD
};

const fmtCLP = (n) =>
  (n === '' || n == null) ? '' : Number(n).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

/* Helpers de Ver Avance */
const sanitizeHtml = (html = '') => String(html || '').replace(/<script/gi, '&lt;script');
const toMinutes = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return (Number.isNaN(h) || Number.isNaN(m)) ? null : h * 60 + m;
};

const getLogDate = (log) => {
  if (log?.date) {
    const dateStr = String(log.date);
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const d = parseSafeDateAny(dateStr);
      if (d) return d;
    }
  }
  return parseSafeDateAny(log?.started_at || log?.startedAt || log?.created_at || log?.createdAt);
};

function getFilteredLogsByPeriod(logs, period) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  return logs.filter(log => {
    const logDate = getLogDate(log);
    if (!logDate) return false;

    switch (period) {
      case 'day':
        return logDate >= today && logDate < tomorrow;
      case 'month':
        return logDate >= thisMonth;
      case 'all':
      default:
        return true;
    }
  });
}

function getPeriodLabel(period) {
  switch (period) {
    case 'day': return 'Hoy';
    case 'month': return 'Este Mes';
    case 'all': return 'Total';
    default: return '';
  }
}


const parseSafeDateAny = (v) => {
  if (!v) return null;
  const t = typeof v === 'string' ? v.replace(' ', 'T') : v;
  const d = new Date(t);
  return Number.isNaN(d) ? null : d;
};
const getHoursFromLog = (l) => {
  if (l?.hours != null) return Number(l.hours) || 0;
  const ms = toMinutes(l?.start), me = toMinutes(l?.end);
  if (ms != null && me != null && me > ms) return Math.round(((me - ms) / 60) * 100) / 100;
  const sa = parseSafeDateAny(l?.started_at || l?.startedAt), ea = parseSafeDateAny(l?.ended_at || l?.endedAt);
  if (sa && ea && ea > sa) return Math.round((((ea - sa) / 1000 / 60 / 60)) * 100) / 100;
  return 0;
};
const getAmountFromLog = (l) => {
  if (l?.amount != null) return Number(l.amount) || 0;
  const rate = Number(l?.rate ?? l?.hourly_rate_snapshot ?? l?.hourly_rate ?? 0) || 0;
  return Math.round(getHoursFromLog(l) * rate);
};

/* ========= Modal: Crear / Editar Proyecto ========= */
function ProjectFormModal({ open, onClose, initial, clients, onSave, saving }) {
  const [form, setForm] = useState({
    client_id: '', name: '', purpose: '',
    default_hourly_rate: '', start_date: '', end_date: '', status: 'active'
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm({
        client_id: initial?.client_id || '',
        name: initial?.name || '',
        purpose: initial?.purpose || '',
        default_hourly_rate: initial?.default_hourly_rate ?? '',
        start_date: initial?.start_date || '',
        end_date: initial?.end_date || '',
        status: normalizeStatus(initial?.status || 'active'),
      });
      setErrors({});
    }
  }, [open, initial]);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const validate = () => {
    const e = {};
    if (!form.client_id) e.client_id = 'Cliente requerido';
    if (!form.name.trim()) e.name = 'Nombre requerido';
    if (form.default_hourly_rate !== '' && form.default_hourly_rate != null) {
      const rate = Number(form.default_hourly_rate);
      if (Number.isNaN(rate) || rate < 0) e.default_hourly_rate = 'Tarifa inválida';
    }
    if (form.start_date && form.end_date && form.start_date > form.end_date) e.end_date = 'Rango inválido';
    setErrors(e); return !Object.keys(e).length;
  };
  const submit = async (ev) => { ev.preventDefault(); if (!validate()) return; await onSave(form); };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={initial ? `Editar proyecto #${initial.id}` : 'Nuevo proyecto'}
      width={1150}
      maxWidth="98vw"
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn" onClick={submit} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </div>
      }
    >
      <form onSubmit={submit} noValidate>
        <FormRow label="Cliente">
          <select value={form.client_id} onChange={(e) => setField('client_id', e.target.value)} style={{ width: '100%' }}>
            <option value="">— Selecciona cliente —</option>
            {(clients || []).filter(c => c.status !== 'deleted').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {errors.client_id && <small className="error">{errors.client_id}</small>}
        </FormRow>
        <FormRow label="Nombre del proyecto">
          <input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Bitácora MVP" />
          {errors.name && <small className="error">{errors.name}</small>}
        </FormRow>
        <FormRow label="Propósito">
          <textarea value={form.purpose} onChange={(e) => setField('purpose', e.target.value)} rows={4} placeholder="Descripción breve del objetivo..." />
        </FormRow>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label="Fecha inicio">
            <input type="date" value={form.start_date} onChange={(e) => setField('start_date', e.target.value)} />
          </FormRow>
          <FormRow label="Fecha término">
            <input type="date" value={form.end_date} onChange={(e) => setField('end_date', e.target.value)} />
            {errors.end_date && <small className="error">{errors.end_date}</small>}
          </FormRow>
        </div>
        <FormRow label="Estado">
          <select value={form.status} onChange={(e) => setField('status', normalizeStatus(e.target.value))} style={{ width: '100%' }}>
            {PROJECT_STATES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </FormRow>
      </form>
    </ModalShell>
  );
}

/* ========= Modal: Miembros ========= */
function MembersModal({ open, onClose, project, token, notify, currentUserId }) {
  const [assigned, setAssigned] = useState([]);
  const [available, setAvailable] = useState([]);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [roleDefault, setRoleDefault] = useState('dev');
  const [draftById, setDraftById] = useState({});

  const load = async () => {
    if (!project) return;
    const [members, users] = await Promise.all([
      listProjectMembersApi(project.id, token),
      listUsersApi(token)
    ]);
    const actives = members.filter(m => m.status === 'active');
    const uniqueByUser = Object.values(
      actives.reduce((acc, m) => { acc[m.user_id] = { ...m, isSelf: m.user_id === currentUserId }; return acc; }, {})
    );
    setAssigned(uniqueByUser);

    setDraftById(Object.fromEntries(uniqueByUser.map(m => [
      m.id, { role_in_project: m.role_in_project, hourly_rate: m.hourly_rate == null ? '' : String(m.hourly_rate) }
    ])));

    const activeIds = new Set(uniqueByUser.map(m => m.user_id));
    setAvailable((users || []).filter(u => !activeIds.has(u.id)));
  };

  useEffect(() => { if (open) { setQ(''); setRoleDefault('dev'); load().catch(() => { }); } }, [open]); // eslint-disable-line

  const filteredAvail = useMemo(() => {
    const s = q.trim().toLowerCase();
    return !s ? available : available.filter(u =>
      [u.name, u.email].some(v => (v || '').toLowerCase().includes(s))
    );
  }, [available, q]);

  const setDraftRole = (mid, role) =>
    setDraftById(s => ({ ...s, [mid]: { ...(s[mid] || {}), role_in_project: role } }));
  const setDraftRate = (mid, val) =>
    setDraftById(s => ({ ...s, [mid]: { ...(s[mid] || {}), hourly_rate: val } }));

  const addUser = (u) => {
    const exists = assigned.some(m => String(m.user_id) === String(u.id));
    if (exists) return;
    const temp = makeTempMember(u, roleDefault);
    setAssigned(prev => [...prev, temp]);
    setDraftById(s => ({ ...s, [temp.id]: { role_in_project: roleDefault, hourly_rate: '' } }));
    setAvailable(prev => prev.filter(x => String(x.id) !== String(u.id)));
  };

  const removeMember = (m) => {
    if (m.isTemp) {
      setAssigned(prev => prev.filter(x => x.id !== m.id));
      setAvailable(prev => [...prev, { id: m.user_id, name: m.user_name, email: m.user_email }]);
      setDraftById(({ [m.id]: _, ...rest }) => rest);
    } else {
      setAssigned(prev => prev.filter(x => x.id !== m.id));
      setDraftById(s => ({ ...s, [m.id]: { ...(s[m.id] || {}), remove: true } }));
    }
  };

  const updateMember = async (m, patch) => {
    setSaving(true);
    try {
      await updateProjectMemberApi(m.id, patch, token);
      await load();
    } catch (e) {
      notify(e?.payload?.error || e.message, true);
    } finally { setSaving(false); }
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const adds = Object.entries(draftById)
        .filter(([id, _]) => String(id).startsWith('tmp-'))
        .map(([id, d]) => {
          const t = assigned.find(x => x.id === id) || { id, user_id: id.replace('tmp-', '') };
          return { id, draft: d, temp: t };
        });

      const reals = Object.entries(draftById)
        .filter(([id, _]) => !String(id).startsWith('tmp-'))
        .map(([id, d]) => ({ id: Number(id), draft: d }));

      for (const { temp, draft } of adds) {
        const body = { user_id: temp.user_id, role_in_project: draft.role_in_project || 'dev' };
        const created = await addProjectMemberApi(project.id, body, token);
        const newId = created?.id;
        const rateStr = (draft.hourly_rate ?? '').toString().trim();
        const rate = rateStr === '' ? null : Math.max(0, Number(rateStr) || 0);
        if (newId != null && rate !== null) {
          await updateProjectMemberApi(newId, { hourly_rate: rate }, token);
        }
      }

      for (const { id, draft } of reals) {
        if (draft?.remove) {
          await updateProjectMemberApi(id, { status: 'inactive' }, token);
        }
      }

      const current = await listProjectMembersApi(project.id, token);
      const currentById = Object.fromEntries((current || []).map(m => [m.id, m]));
      for (const { id, draft } of reals) {
        if (!draft || draft.remove) continue;
        const orig = currentById[id];
        if (!orig) continue;
        const roleChanged = draft.role_in_project && draft.role_in_project !== orig.role_in_project;
        const rateStr = (draft.hourly_rate ?? '').toString().trim();
        const newRate = rateStr === '' ? null : Math.max(0, Number(rateStr) || 0);
        const rateChanged = (newRate ?? null) !== (orig.hourly_rate ?? null);
        if (roleChanged || rateChanged) {
          const patch = {};
          if (roleChanged) patch.role_in_project = draft.role_in_project;
          if (rateChanged) patch.hourly_rate = newRate;
          await updateProjectMemberApi(id, patch, token);
        }
      }

      await load();
      notify('Cambios guardados');
    } catch (e) {
      notify(e?.payload?.error || e.message, true);
    } finally { setSaving(false); }
  };

  return (
    <ModalShell
      open={open} onClose={onClose}
      title={`Miembros — ${project?.name || ''}`}
      width={1150} maxWidth="98vw"
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn" onClick={saveChanges} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      }
    >
      {!project ? null : (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {/* Disponibles */}
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <strong>Disponibles</strong>
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar usuario…" style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 8 }} />
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, background: '#fff' }}>
              {filteredAvail.map(u => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f1f5f9', padding: '8px 4px' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{u.name}</div>
                    <small style={{ opacity: .8 }}>{u.email}</small>
                  </div>
                  <button className="btn" onClick={() => addUser(u)} disabled={saving}>Agregar</button>
                </div>
              ))}
              {!filteredAvail.length && <div style={{ opacity: .6 }}>Sin usuarios</div>}
            </div>
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 12, opacity: .8 }}>Rol por defecto</label>
              <select value={roleDefault} onChange={(e) => setRoleDefault(e.target.value)} style={{ width: '100%' }}>
                <option value="lead">lead</option>
                <option value="dev">dev</option>
                <option value="qa">qa</option>
                <option value="pm">pm</option>
              </select>
            </div>
          </div>

          {/* Asignados */}
          <div style={{ flex: 1, minWidth: 280 }}>
            <strong>Asignados</strong>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', marginTop: 8 }}>
              {assigned.map(m => {
                const d = draftById[m.id] || { role_in_project: m.role_in_project, hourly_rate: m.hourly_rate == null ? '' : String(m.hourly_rate) };
                return (
                  <div key={m.id} style={{ borderTop: '1px solid #f1f5f9', padding: '10px 8px' }}>
                    <div style={{ fontWeight: 600 }}>{m.user_name}</div>
                    <small style={{ opacity: .8 }}>{m.user_email}</small>

                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px,1fr) minmax(160px,1fr) 56px', gap: 8, alignItems: 'center', marginTop: 8 }}>
                      {/* Rol */}
                      <select
                        value={(draftById[m.id]?.role_in_project) ?? m.role_in_project}
                        onChange={(e) => setDraftRole(m.id, e.target.value)}
                        disabled={saving}
                        style={{ width: '100%' }}
                      >
                        <option value="lead">lead</option>
                        <option value="dev">dev</option>
                        <option value="qa">qa</option>
                        <option value="pm">pm</option>
                      </select>

                      {/* Tarifa */}
                      <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr', alignItems: 'center', gap: 6 }}>
                        <span style={{ border: '1px solid #e5e7eb', background: '#f8fafc', padding: '6px 8px', borderRadius: 8, textAlign: 'center' }}>$ / h</span>
                        <input
                          type="number" min="0" step="1" placeholder="0"
                          value={(d.hourly_rate)}
                          onChange={(e) => setDraftRate(m.id, e.target.value)}
                          disabled={saving}
                          style={{ padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 8 }}
                        />
                      </div>

                      {/* Papelera */}
                      <button
                        className="btn"
                        onClick={() => removeMember(m)}
                        disabled={saving || m.isSelf}
                        title={m.isSelf ? 'No puedes quitarte a ti mismo' : 'Quitar'}
                        aria-label="Quitar"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })}
              {!assigned.length && <div style={{ padding: 10, opacity: .6 }}>Sin miembros asignados</div>}
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

/* ========= Modal: Registrar horas ========= */
function Field({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto auto', gap: 6, minWidth: 0 }}>
      <label style={{ fontSize: 14, fontWeight: 600 }}>{label}</label>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

/* Guardado temporal */
function makeTempMember(u, role) {
  return {
    id: `tmp-${u.id}`,
    user_id: u.id,
    user_name: u.name,
    user_email: u.email,
    role_in_project: role,
    hourly_rate: null,
    isTemp: true,
  };
}

/* ========= Tabla con expansión de “Últimas entradas” ========= */
function ProjectsTable({ rows, isAdmin, logsByProject, expanded, onToggleExpand, onEdit, onMembers, onViewLog, onRefreshLogs, logsLimitByProject, onLoadMoreLogs, onViewAdvance, page, pageSize, totalCount, onPageChange, periodFilter, currentUserId }) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIdx = (page - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalCount);
  
  const getFilteredLogsForUser = (projectId) => {
    const logs = logsByProject[projectId] || [];
    
    if (isAdmin) {
      return logs;
    }
    
    return logs.filter(log => {
      const logUserId = String(log?.user_id || log?.userId || log?.user?.id || '');
      return logUserId === currentUserId;
    });
  };
  
  const getProjectTotal = (projectId) => {
    const userLogs = getFilteredLogsForUser(projectId);
    const filteredLogs = getFilteredLogsByPeriod(userLogs, periodFilter);
    return filteredLogs.reduce((sum, log) => sum + getAmountFromLog(log), 0);
  };
  
  const getProjectHours = (projectId) => {
    const userLogs = getFilteredLogsForUser(projectId);
    const filteredLogs = getFilteredLogsByPeriod(userLogs, periodFilter);
    return filteredLogs.reduce((sum, log) => sum + getHoursFromLog(log), 0);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rows;

    return [...rows].sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === 'hours') {
        aValue = getProjectHours(a.id);
        bValue = getProjectHours(b.id);
      } else if (sortConfig.key === 'total') {
        aValue = getProjectTotal(a.id);
        bValue = getProjectTotal(b.id);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [rows, sortConfig, periodFilter]) ;

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span style={{ opacity: 0.3, marginLeft: 4 }}>⇅</span>;
    }
    return (
      <span style={{ marginLeft: 4 }}>
        {sortConfig.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };


  return (
    <div className="data-table-container">
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th className="col-date" style={{ width: '70px', textAlign: 'center' }}>ID</th>
              <th className="col-project" style={{ width: '22%' }}>Proyecto</th>
              <th className="col-member" style={{ width: '16%' }}>Cliente</th>
              <th className="col-hours" style={{ width: '9%', textAlign: 'center' }}>Miembros</th>
              <th className="col-rate" style={{ width: '10%', textAlign: 'center' }}>Estado</th>
              <th 
                className="col-hours" 
                style={{ width: '10%', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('hours')}
              >
                Horas {!isAdmin && <span style={{ fontSize: '11px', opacity: 0.7 }}>(Mis)</span>}
                <SortIcon columnKey="hours" />
              </th>
              <th 
                className="col-amount" 
                style={{ width: '11%', textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('total')}
              >
                Total {!isAdmin && <span style={{ fontSize: '11px', opacity: 0.7 }}>(Mis)</span>}
                <SortIcon columnKey="total" />
              </th>
              <th className="col-actions" style={{ width: '22%', textAlign: 'center' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(r => {
              const projectHours = getProjectHours(r.id);
              const projectTotal = getProjectTotal(r.id);
              
              return (
                <Fragment key={r.id}>
                  <tr>
                    <td className="col-date" style={{ textAlign: 'center', fontWeight: 600 }}>{r.id}</td>
                    <td className="col-project" style={{ fontWeight: 600, color: '#1e293b' }}>
                      {r.name}
                    </td>
                    <td className="col-member">{r.client_name}</td>
                    <td className="col-hours" style={{ textAlign: 'center' }}>{r.member_count ?? '-'}</td>
                    <td className="col-rate" style={{ textAlign: 'center' }}><StatusPill status={r.status} /></td>
                    <td className="col-hours" style={{ textAlign: 'right', fontWeight: 600, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                      {projectHours > 0 ? `${projectHours.toFixed(2)} h` : '—'}
                    </td>
                    <td className="col-amount" style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                      {projectTotal > 0 ? fmtCLP(projectTotal) : '—'}
                    </td>
                    <td className="col-actions">
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {isAdmin && (
                          <>
                            <button className="btn" style={btnSecondary} title="Editar" onClick={() => onEdit(r)}>
                              <FaPen /><span>Editar</span>
                            </button>
                            <button className="btn" style={btnSecondary} title="Miembros" onClick={() => onMembers(r)}>
                              <FaUsers /><span>Miembros</span>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>

                  {expanded.has(r.id) && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0, background: '#f8fafc' }}>
                        <div style={{ padding: '16px', borderTop: '2px solid #e2e8f0' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                            <strong style={{ fontSize: '15px', color: '#1e293b' }}>
                              {isAdmin ? 'Últimas entradas' : 'Mis últimas entradas'}
                            </strong>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <button className="btn" style={btnSecondary} onClick={() => onRefreshLogs(r.id)} title="Refrescar">
                                <FaSyncAlt /><span>Refrescar</span>
                              </button>
                              {(logsLimitByProject?.[r.id] ?? 5) < 50 && (
                                <button className="btn" style={btnSecondary} onClick={() => onLoadMoreLogs(r.id)} title="Mostrar más">
                                  <FaBookOpen /><span>Mostrar más (+5)</span>
                                </button>
                              )}
                              <button className="btn" style={btnPrimary} onClick={() => onViewLog(r)} title="Bitácora del proyecto">
                                <FaBookOpen /> <span>Bitácora</span>
                              </button>
                            </div>
                          </div>

                          <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            {(() => {
                              const displayLogs = getFilteredLogsForUser(r.id);
                              return displayLogs.length ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: '#e2e8f0' }}>
                                  {displayLogs.map(l => (
                                    <div key={l.id} style={{ 
                                      display: 'grid', 
                                      gridTemplateColumns: 'auto 1fr auto auto', 
                                      gap: '16px', 
                                      alignItems: 'center', 
                                      padding: '12px 16px', 
                                      background: 'white',
                                      fontSize: '14px'
                                    }}>
                                      {/* Fecha */}
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', color: '#64748b' }}>
                                        <FaCalendarAlt /><span>{fmtDate(l.date)}</span>
                                      </div>

                                      {/* Usuario + rango hora · actividad opcional */}
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flexWrap: 'wrap' }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                          <FaUser style={{ color: '#64748b' }} />
                                          <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#1e293b' }}>
                                            {l.user_name}
                                          </span>
                                        </span>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#64748b' }}>
                                          <FaRegClock />{l.start}–{l.end}
                                        </span>
                                        {l.activity_code && (
                                          <span style={{ background: '#eef2ff', border: '1px solid #c7d2fe', padding: '2px 8px', borderRadius: 999, fontSize: 12, color: '#4f46e5', fontWeight: 500 }}>
                                            {l.activity_code}
                                          </span>
                                        )}
                                        {!l.billable && (
                                          <span style={{ background: '#fee2e2', border: '1px solid #fecaca', padding: '2px 8px', borderRadius: 999, fontSize: 12, color: '#dc2626', fontWeight: 500 }}>
                                            No facturable
                                          </span>
                                        )}
                                      </div>

                                      {/* Ver Avance button */}
                                      <div>
                                        <button className="btn" style={btnIconRow} onClick={()=>onViewAdvance({ ...l, project_name: r.name })}>
                                          <FaEye /> Ver Avance
                                        </button>
                                      </div>

                                      {/* HH + $/h + Total */}
                                      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#1e293b' }}>
                                        <div style={{ fontWeight: 600 }}>{Number(l.hours).toFixed(2)} h</div>
                                        <div style={{ fontSize: '13px', color: '#64748b' }}>{fmtCLP(l.rate)}</div>
                                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{fmtCLP(l.amount)}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                                  {isAdmin ? 'No hay entradas aún.' : 'No tienes entradas en este proyecto.'}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!sortedRows.length && <tr><td colSpan="8" style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>Sin proyectos</td></tr>}
          </tbody>
        </table>
      </div>
      
      {/* Paginación */}
      {totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '16px 20px',
          borderTop: '1px solid #e2e8f0',
          background: '#f8fafc',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            Mostrando {startIdx + 1}-{endIdx} de {totalCount} proyectos
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button 
              className="btn" 
              style={{
                ...btnSecondary,
                opacity: page === 1 ? 0.5 : 1,
                cursor: page === 1 ? 'not-allowed' : 'pointer'
              }}
              onClick={() => page > 1 && onPageChange(page - 1)}
              disabled={page === 1}
            >
              ← Anterior
            </button>
            <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 500, padding: '0 8px' }}>
              Página {page} de {totalPages}
            </span>
            <button 
              className="btn" 
              style={{
                ...btnSecondary,
                opacity: page === totalPages ? 0.5 : 1,
                cursor: page === totalPages ? 'not-allowed' : 'pointer'
              }}
              onClick={() => page < totalPages && onPageChange(page + 1)}
              disabled={page === totalPages}
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========= Página principal ========= */
export default function Projects() {
  const { token, user, hasRole } = useAuth();
  const isAdmin = hasRole ? hasRole('admin') : user?.role === 'admin';
  const currentUserId = user?.id != null ? String(user.id) : '';

  // Bitácora
  const [bitacora, setBitacora] = useState({ open: false, project: null });
  const navigate = useNavigate();
  const openBitacora = (p = null) => {
    if (p?.id) return navigate(`/worklog/${p.id}`);
    return navigate('/bitacora');
  };
  // Modal “Ver avance”
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLog, setViewLog] = useState(null);
  const openViewAdvance = (log, projectRow=null)=>{
    const project_name = projectRow?.name || log?.project_name || log?.project;
    setViewLog(project_name ? { ...log, project_name } : log);
    setViewOpen(true);
  };
  const closeViewAdvance = () => { setViewOpen(false); setViewLog(null); };

  const [banner, setBanner] = useState({ kind: 'info', msg: '' });
  const notify = (msg, isErr = false) => setBanner({ kind: isErr ? 'error' : 'info', msg });

  // filtros
  const [q, setQ] = useState('');
  const [clientId, setClientId] = useState('');
  const [status, setStatus] = useState('');
  const [mine, setMine] = useState(isAdmin ? false : true);

  // paginación
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);

  // data
  const [clients, setClients] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // cache de logs por proyecto
  const [logsByProject, setLogsByProject] = useState({});
  const [logsLimitByProject, setLogsLimitByProject] = useState({}); //(default 5)

  // Filtro de período
  const [periodFilter, setPeriodFilter] = useState('month');


  // const refresh = async () => {
  //   setLoading(true);
  //   try {
  //     let ps = [];
  //     let cs = [];
  //     if (isAdmin) {
  //       [ps, cs] = await Promise.all([
  //         listProjectsApi(token, { mine }),
  //         listClientsApi(token)
  //       ]);
  //     } else {
  //       ps = await listProjectsApi(token, { mine: true });
  //       cs = [];
  //     }
  //     setRows(Array.isArray(ps) ? ps : []);
  //     setClients(Array.isArray(cs) ? cs : []);
  //   } catch (err) {
  //     const msg = err?.payload?.error || err?.message || '';
  //     if (!isAdmin && !mine) {
  //       try {
  //         const ps2 = await listProjectsApi(token, { mine: true });
  //         setRows(Array.isArray(ps2) ? ps2 : []);
  //         setClients([]);
  //         setMine(true);
  //       } catch (e2) {
  //         notify(e2?.payload?.error || e2.message, true);
  //       }
  //     } else {
  //       notify(msg, true);
  //     }
  //   } finally { setLoading(false); }
  // };

  const refresh = async () => {
  setLoading(true);
  try {
    let ps = [];
    let cs = [];
    if (isAdmin) {
      [ps, cs] = await Promise.all([
        listProjectsApi(token, { mine }),
        listClientsApi(token)
      ]);
    } else {
      ps = await listProjectsApi(token, { mine: true });
      cs = [];
    }
    setRows(Array.isArray(ps) ? ps : []);
    setClients(Array.isArray(cs) ? cs : []);
    
    if (Array.isArray(ps) && ps.length > 0) {
      const logsPromises = ps.map(async (p) => {
        try {
          const logs = await listProjectLogsApi(p.id, token, 500);
          return { projectId: p.id, logs };
        } catch (err) {
          console.error(`Error cargando logs del proyecto ${p.id}:`, err);
          return { projectId: p.id, logs: [] };
        }
      });
      
      const logsResults = await Promise.all(logsPromises);
      const logsMap = {};
      const limitsMap = {};
      
      logsResults.forEach(({ projectId, logs }) => {
        logsMap[projectId] = logs;
        limitsMap[projectId] = 5;
      });
      
      setLogsByProject(logsMap);
      setLogsLimitByProject(limitsMap);
    }
  } catch (err) {
    const msg = err?.payload?.error || err?.message || '';
    if (!isAdmin && !mine) {
      try {
        const ps2 = await listProjectsApi(token, { mine: true });
        setRows(Array.isArray(ps2) ? ps2 : []);
        setClients([]);
        setMine(true);
      } catch (e2) {
        notify(e2?.payload?.error || e2.message, true);
      }
    } else {
      notify(msg, true);
    }
  } finally { setLoading(false); }
};

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [mine, isAdmin]);

  // expand/collapse y carga de logs
  const [expanded, setExpanded] = useState(new Set());
  const toggleExpand = async (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    if (!logsByProject[id]) { await refreshProjectLogs(id); }
  };

  // modales
  const [projectModal, setProjectModal] = useState({ open: false, initial: null, saving: false });
  const [membersModal, setMembersModal] = useState({ open: false, project: null });

  // CRUD proyecto
  const openCreate = () => setProjectModal({ open: true, initial: null, saving: false });
  const openEdit = (row) => setProjectModal({ open: true, initial: row, saving: false });
  const closeProjectModal = () => setProjectModal({ open: false, initial: null, saving: false });

  const saveProject = async (form) => {
    try {
      setProjectModal(s => ({ ...s, saving: true }));

      // helpers: saneo de payload
      const toNull = (v) => (v === '' || v === undefined) ? null : v;
      const sanitizePayload = (f) => {
        const p = {
          name: (f.name || '').trim(),
          purpose: toNull((f.purpose || '').trim()),
          client_id: f.client_id ? Number(f.client_id) : null,
          status: normalizeStatus(f.status || 'active'),
          start_date: toNull(f.start_date),
          end_date: toNull(f.end_date),
          default_hourly_rate:
            toNull(f.default_hourly_rate) == null
              ? null
              : Math.max(0, Number(f.default_hourly_rate) || 0),
        };
        Object.keys(p).forEach(k => p[k] === null && delete p[k]);
        return p;
      };

      const was = projectModal.initial || null;
      const payload = sanitizePayload(form);

      // detectar si SOLO cambió el status
      const statusChanged = !!was && payload.status != null && normalizeStatus(payload.status) !== normalizeStatus(was.status);
      const otherKeys = ['name','purpose','client_id','start_date','end_date','default_hourly_rate'];
      const anyOtherChanged = !!was && otherKeys.some(k => {
        const prev = sanitizePayload(was)[k];
        const curr = payload[k];
        return (curr !== undefined) && (String(curr) !== String(prev));
      });

      if (projectModal.initial) {
        if (statusChanged && !anyOtherChanged) {
          try {
            await setProjectStatusApi(projectModal.initial.id, payload.status, token);
          } catch (e1) {
            // fallback por si el backend espera 'on-hold'
            const alt = payload.status === 'on_hold' ? 'on-hold' : payload.status;
            if (alt !== payload.status) {
              await setProjectStatusApi(projectModal.initial.id, alt, token);
            } else {
              throw e1;
            }
          }
        } else {
          await updateProjectApi(projectModal.initial.id, payload, token);
        }
        toast.success('Proyecto actualizado exitosamente');
      } else {
        await createProjectApi(payload, token);
        toast.success('Proyecto creado exitosamente');
      }
      closeProjectModal();
      await refresh();
    } catch (err) {
      toast.error(err?.payload?.error || err.message || 'Error al guardar el proyecto');
      setProjectModal(s => ({ ...s, saving: false }));
    }
  };
  // filtro local
  const filtered = useMemo(() => {
    let data = [...rows];

    // búsqueda
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      data = data.filter(r =>
        [r.name, r.client_name, r.status].map(x => (x || '').toString().toLowerCase()).some(v => v.includes(s))
      );
    }

    // cliente
    if (clientId) data = data.filter(r => String(r.client_id) === String(clientId));

    // estado ('' = todos)
    if (status !== '') {
      const wanted = normalizeStatus(status);
      data = data.filter(r => normalizeStatus(r.status) === wanted);
    }

    return data;
  }, [rows, q, clientId, status]);

  useEffect(() => {
    setPage(1);
  }, [q, clientId, status]);

  const paginatedData = useMemo(() => {
    const startIdx = (page - 1) * pageSize;
    return filtered.slice(startIdx, startIdx + pageSize);
  }, [filtered, page, pageSize]);

  const refreshProjectLogs = async (pid, limit) => {
    const lim = limit ?? logsLimitByProject[pid] ?? 5;
    const logs = await listProjectLogsApi(pid, token, lim);
    setLogsByProject(s => ({ ...s, [pid]: logs }));
    setLogsLimitByProject(s => ({ ...s, [pid]: lim }));
  };

  const loadMoreLogs = async (pid, step = 5) => {
    const current = logsLimitByProject[pid] ?? 5;
    const next = Math.min(current + step, 50);
    await refreshProjectLogs(pid, next);
  };

  return (
    <Layout title="Proyectos" wide>
      <Toaster position="top-center" reverseOrder={false} />
      <div className="container-tw">
        {/* Toolbar */}
        <div className="toolbar">
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Listado de Proyectos</h3>
          <div className="toolbar-actions">
            {isAdmin && (
              <button className="btn" onClick={() => setProjectModal({ open: true, initial: null, saving: false })} style={btnPrimary} title="Crear proyecto">
                <FaPlus /><span>Nuevo Proyecto</span>
              </button>
            )}
          </div>
        </div>

        <Banner kind={banner.kind} onClose={() => setBanner({ kind: 'info', msg: '' })}>{banner.msg}</Banner>

        {/* Filters Card */}
        <div className="filters-card">
          <div className="flex flex-col gap-2 max-w-3xl">
            <FormRow label="Buscar proyecto">
              <input
                type="search"
                placeholder="Buscar proyecto o cliente…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </FormRow>

            {isAdmin && (
              <FormRow label="Cliente">
                <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
                  <option value="">Todos los clientes</option>
                  {(clients || []).filter(c => c.status !== 'deleted').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </FormRow>
            )}

            <FormRow label="Estado">
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Todos los estados</option>
                {PROJECT_STATES.map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </FormRow>
          </div>
        </div>

                {/* Filtro de Período */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          padding: '1rem',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: '12px',
          border: '2px solid #e2e8f0'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#1e293b' }}>
            Estadísticas de {getPeriodLabel(periodFilter)}
          </h3>
          <select 
            value={periodFilter} 
            onChange={(e) => setPeriodFilter(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: '2px solid #e5e7eb',
              background: 'white',
              color: '#1f2937',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s ease'
            }}
          >
            <option value="day">Hoy</option>
            <option value="month">Este Mes</option>
            <option value="all">Todo</option>
          </select>
        </div>

        {/* Tabla */}
        <div>
          <ProjectsTable
            rows={paginatedData}
            isAdmin={isAdmin}
            logsByProject={logsByProject}
            expanded={expanded}
            onToggleExpand={(id)=>setExpanded(prev=>{const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n;})}
            onEdit={(row)=>setProjectModal({ open: true, initial: row, saving: false })}
            onMembers={(r) => setMembersModal({ open: true, project: r })}
            onViewLog={openBitacora}
            onRefreshLogs={refreshProjectLogs}
            logsLimitByProject={logsLimitByProject}
            onLoadMoreLogs={loadMoreLogs}
            onViewAdvance={(log)=>openViewAdvance(log)}
            page={page}
            pageSize={pageSize}
            totalCount={filtered.length}
            onPageChange={setPage}
            periodFilter={periodFilter}
            currentUserId={currentUserId}
          />
          {loading && <div style={{ marginTop: 16, textAlign: 'center', color: '#64748b', fontSize: '14px' }}>Cargando…</div>}
        </div>
      </div>

      {/* Modales */}
      <ProjectFormModal
        open={projectModal.open}
        initial={projectModal.initial}
        clients={clients}
        saving={projectModal.saving}
        onSave={saveProject}
        onClose={closeProjectModal}
      />
      <MembersModal
        open={membersModal.open}
        project={membersModal.project}
        token={token}
        currentUserId={user?.id}
        notify={notify}
        onClose={() => setMembersModal({ open: false, project: null })}
      />
      {/* ===== Modal: Ver Avance ===== */}
      {viewOpen && (
        <ModalShell open={viewOpen} onClose={closeViewAdvance} title="Avance de bitácora">
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: 16, 
            marginBottom: 20 
          }}>
            <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: 4, fontWeight: 600 }}>Fecha</div>
              <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: 500 }}>
                {String(viewLog?.date || '').slice(0,10)} {viewLog?.start ? ` ${viewLog.start}` : ''}
              </div>
            </div>
            <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: 4, fontWeight: 600 }}>Programador</div>
              <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: 500 }}>
                {viewLog?.user_name || viewLog?.user?.name || '—'}
              </div>
            </div>
            <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: 4, fontWeight: 600 }}>Horas</div>
              <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                {getHoursFromLog(viewLog).toFixed(2)}
              </div>
            </div>
            <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: 4, fontWeight: 600 }}>Monto</div>
              <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                {fmtCLP(getAmountFromLog(viewLog))}
              </div>
            </div>
            {viewLog?.activity_code && (
              <div style={{ padding: '12px 16px', background: '#eef2ff', borderRadius: 8, border: '1px solid #c7d2fe' }}>
                <div style={{ fontSize: '12px', color: '#4f46e5', marginBottom: 4, fontWeight: 600 }}>Etiqueta</div>
                <div style={{ fontSize: '14px', color: '#4338ca', fontWeight: 500 }}>
                  {viewLog.activity_code}
                </div>
              </div>
            )}
            {!viewLog?.billable && (
              <div style={{ padding: '12px 16px', background: '#fee2e2', borderRadius: 8, border: '1px solid #fecaca' }}>
                <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: 4, fontWeight: 600 }}>Facturable</div>
                <div style={{ fontSize: '14px', color: '#b91c1c', fontWeight: 500 }}>No</div>
              </div>
            )}
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: 8, fontWeight: 600 }}>Nota de Avance</div>
            <div
              style={{ 
                border: '1px solid #e2e8f0', 
                borderRadius: 8, 
                padding: 16, 
                maxHeight: 320, 
                overflow: 'auto',
                background: '#ffffff',
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#334155'
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(viewLog?.progress_note || viewLog?.note || '<p style="color: #94a3b8;">Sin nota de avance</p>') }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20, gap: 8 }}>
            <button className="btn" style={btnSecondary} onClick={closeViewAdvance}>Cerrar</button>
          </div>
        </ModalShell>
      )}
    </Layout>
  );
}
